import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import ClaimSuccessModal from "./SupportSuccessModal";
import config from "../../../config/config";
import {
  FaExclamationCircle,
  FaTimesCircle,
  FaCheckCircle,
  FaArrowLeft,
  FaLock,
  FaRedo,
} from "react-icons/fa";
import { SlEqualizer } from "react-icons/sl";
import { useMediaQuery } from "react-responsive";
import { IoMdClose } from "react-icons/io";
import { useInvoice } from "../../../context/InvoiceContext";
import { GrPowerCycle } from "react-icons/gr";
import axiosInstance from "../../../AxiosInstance";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RotatingLines } from "react-loader-spinner";

const MyInvoicesContent = () => {
  const navigate = useNavigate();
  const { invoicesData, fetchAllInvoices } = useInvoice();
  const [invoiceId, setInvoiceId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(7);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [filterInvoiceId, setFilterInvoiceId] = useState("");
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterInvoiceSent, setFilterInvoiceSent] = useState("");
  const [filterInvoiceStatus, setFilterInvoiceStatus] = useState("");
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isCollectingPayment, setIsCollectingPayment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [showFilter, setShowFilter] = useState(false);

  const tableContainerRef = useRef(null);
  const tableRowRef = useRef(null);

  const baseURL = config.API_BASE_URL;

  const getQueryParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathParts = window.location.pathname.split("/");
    const invoiceIdFromPath = pathParts[pathParts.length - 1];
    return { invoiceId: invoiceIdFromPath };
  };

  useEffect(() => {
    const { invoiceId } = getQueryParams();
    setInvoiceId(invoiceId);
  }, []);

  useEffect(() => {
    const loadInvoices = async () => {
      setLoading(true);
      await fetchAllInvoices();
      setLoading(false);
    };

    loadInvoices();

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        await loadInvoices();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchAllInvoices]);

  useEffect(() => {
    if (invoiceId && invoicesData.length > 0) {
      const selectedInvoice = invoicesData.find(
        (invoice) => invoice.invoiceId === invoiceId
      );
      if (selectedInvoice) {
        setSelectedInvoice(selectedInvoice);
        setIsModalOpen(true);
      }
    }
  }, [invoiceId, invoicesData]);

  useEffect(() => {
    filterInvoices();
  }, [
    filterName,
    filterInvoiceId,
    filterStartDate,
    filterEndDate,
    filterStatus,
    filterInvoiceSent,
    filterInvoiceStatus,
    invoicesData,
  ]);

  useEffect(() => {
    const adjustItemsPerPage = () => {
      if (tableContainerRef.current && tableRowRef.current) {
        const containerHeight = tableContainerRef.current.offsetHeight;
        const rowHeight = tableRowRef.current.offsetHeight;

        let headerHeight = document.querySelector(
          ".supportclaim-header"
        )?.offsetHeight;
        headerHeight += rowHeight;

        const paginationHeight = document.querySelector(
          ".supportclaim-pagination-controls"
        )?.offsetHeight;

        const availableHeight =
          containerHeight - headerHeight - paginationHeight - rowHeight * 3;
        const visibleRows = Math.floor(availableHeight / rowHeight);

        setItemsPerPage(visibleRows || 7);
      }
    };

    adjustItemsPerPage();

    window.addEventListener("resize", adjustItemsPerPage);

    return () => {
      window.removeEventListener("resize", adjustItemsPerPage);
    };
  }, [invoicesData]);

  const generateDropdownOptions = () => {
    let base = itemsPerPage;
    let secondOption = Math.ceil((base * 2) / 5) * 5;
    return [base, secondOption, secondOption * 2].filter(
      (value, index, self) => self.indexOf(value) === index
    );
  };

  const dropdownOptions = generateDropdownOptions();

  const lastItemIndex = currentPage * itemsPerPage;
  const firstItemIndex = lastItemIndex - itemsPerPage;
  const currentItems = filteredInvoices.slice(firstItemIndex, lastItemIndex);
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handleModalClose = () => {
    setShowModal(false);
    navigate(`/account/admin-dashboard/billing-invoicing/invoices`);
  };

  const handleBackClick = () => {
    setIsModalOpen(false);
    navigate(`/account/admin-dashboard/billing-invoicing/invoices`);
  };

  const handleFilterChange = (filterSetter) => (e) => {
    filterSetter(e.target.value);
  };

  const handleInvoiceStatusFilterChange = (event) => {
    setFilterInvoiceStatus(event.target.value);
  };

  const filterInvoices = () => {
    const filtered = invoicesData.filter((invoice) => {
      const invoiceDate = new Date(invoice.created * 1000);
      const endDate = new Date(filterEndDate);
      endDate.setHours(23, 59, 59, 999);
      const lowerCaseFilterName = filterName.toLowerCase();
      return (
        (filterName === "" ||
          invoice.clientName.toLowerCase().includes(lowerCaseFilterName) ||
          invoice.workerName.toLowerCase().includes(lowerCaseFilterName)) &&
        (filterInvoiceId === "" ||
          invoice.invoiceId.includes(filterInvoiceId)) &&
        (filterStartDate === null || invoiceDate >= filterStartDate) &&
        (filterEndDate === null || invoiceDate <= endDate) &&
        (filterStatus === "" || invoice.paymentStatus === filterStatus) &&
        (filterInvoiceSent === "" ||
          (filterInvoiceSent === "Yes" ? invoice.paidAt : !invoice.paidAt)) &&
        (filterInvoiceStatus === "" ||
          (filterInvoiceStatus === "true"
            ? invoice.invoiceSent
            : !invoice.invoiceSent))
      );
    });

    setFilteredInvoices(filtered);
  };

  const handleClearFilters = () => {
    setFilterName("");
    setFilterInvoiceId("");
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterStatus("");
    setFilterInvoiceSent("");
    setSelectedInvoices([]);
  };

  const handleViewMore = async (invoiceId) => {
    setSelectedInvoice(
      filteredInvoices.find((invoice) => invoice.invoiceId === invoiceId)
    );
    setIsModalOpen(true);
    navigate(
      `/account/admin-dashboard/billing-invoicing/invoices/details/${invoiceId}`
    );
  };

  const selectableInvoices = filteredInvoices.filter(
    (invoice) => invoice.paymentStatus === "open"
  );

  const handleSelectAll = () => {
    console.log("Toggle Select All");
    if (selectedInvoices.length === selectableInvoices.length) {
      setSelectedInvoices([]);
      console.log("Deselected All Invoices");
    } else {
      const allSelectableInvoices = selectableInvoices.map(
        (invoice) => invoice.invoiceId
      );
      setSelectedInvoices(allSelectableInvoices);
      console.log("Selected All Invoices:", allSelectableInvoices);
    }
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices((prevSelectedInvoices) => {
      const isAlreadySelected = prevSelectedInvoices.includes(invoiceId);
      const newSelectedInvoices = isAlreadySelected
        ? prevSelectedInvoices.filter((id) => id !== invoiceId)
        : [...prevSelectedInvoices, invoiceId];

      const selectedInvoice = allInvoices.find(
        (invoice) => invoice.invoiceId === invoiceId
      );

      if (isAlreadySelected) {
        console.log(`Invoice deselected:`, selectedInvoice);
      } else {
        console.log(`Invoice selected:`, selectedInvoice);
      }
      console.log("Updated Selected Invoices:", newSelectedInvoices);
      return newSelectedInvoices;
    });
  };

  const handlePaySelectedInvoices = async () => {
    const selectedInvoicesData = selectedInvoices.map((invoiceId) =>
      allInvoices.find((invoice) => invoice.invoiceId === invoiceId)
    );
    await handlePayInvoices(selectedInvoicesData);
  };

  const handlePayInvoices = async (invoices) => {
    const successMessages = [];
    for (const invoice of invoices) {
      await processPayment(invoice, (message) => {
        if (message.includes("successfully")) {
          successMessages.push(invoice.invoiceId);
        } else {
          setSuccessMessage(message);
        }
      });
    }
    if (successMessages.length > 0) {
      setSuccessMessage("Payment processed successfully!");
    }
    setSelectedInvoices([]);
  };

  const formatStatus = (status) => {
    if (!status) return "";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const handlePayInvoice = async (invoice) => {
    await handlePayInvoices([invoice]);
  };

  const processPayment = async (invoice, onSuccess) => {
    const smoothScrollToTop = () => {
      const element = tableContainerRef.current;
      if (element) {
        const scrollDuration = 600;
        const scrollStep = -element.scrollTop / (scrollDuration / 15);
        const scrollInterval = setInterval(() => {
          if (element.scrollTop !== 0) {
            element.scrollTop += scrollStep;
          } else {
            clearInterval(scrollInterval);
          }
        }, 15);
      }
    };

    try {
      smoothScrollToTop();
      setIsCollectingPayment(true);
      setProgress(30);
      onSuccess("Processing payment...");

      const response = await axiosInstance.post(
        `${baseURL}/v1/stripe/pay-invoice`,
        {
          invoiceId: invoice.invoiceId,
        }
      );

      if (response.data.success) {
        const paymentIntentId = response.data.paymentIntentId;
        setProgress(60);
        onSuccess("Processing payment...");

        const updateResponse = await axiosInstance.patch(
          `${baseURL}/v1/invoices/${invoice.invoiceId}`,
          {
            caseType: "pay_invoice",
          }
        );

        if (updateResponse.data) {
          setProgress(80);
          onSuccess("Almost there! We're finalizing your payment...");

          try {
            await axiosInstance.post(
              `${baseURL}/v1/stripe/save-payment-intent`,
              { paymentIntentId },
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );
            setProgress(100);
            onSuccess("Payment processed successfully!");
          } catch (saveError) {
            console.error("Error saving payment intent:", saveError);
            onSuccess(
              "Payment processed successfully, but failed to save payment intent."
            );
          }
        } else {
          onSuccess(
            `Failed to update local database: ${updateResponse.data.description}`
          );
        }
      } else {
        onSuccess(`Failed to process invoice: ${response.data.description}`);
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      onSuccess("Error processing payment.");
    } finally {
      setPaymentProcessed(true);
      setProgress(0);
      setIsCollectingPayment(false);
    }
  };

  const isPaySelectedInvoicesDisabled = selectedInvoices.every((invoiceId) => {
    const invoice = allInvoices.find(
      (invoice) => invoice.invoiceId === invoiceId
    );
    return (
      invoice.status === "paid" &&
      invoice.paymentStatus === "paid" &&
      invoice.invoiceSent &&
      invoice.invoicePaid
    );
  });

  const handleClose = () => {
    setPaymentProcessed(false);
    setSuccessMessage("");
  };

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  return (
    <div>
      <div ref={tableContainerRef} className="supportinvoice-support-container">
        {(paymentProcessed || isCollectingPayment) && (
          <div className="success-message-container">
            {paymentProcessed && (
              <div className="claim-success-message">
                <FaCheckCircle className="invoice-check-icon" />
                <h4>{successMessage}</h4>
                <IoMdClose
                  className="success-message-close"
                  onClick={handleClose}
                />
              </div>
            )}

            {isCollectingPayment && (
              <div className="invoice-success-progress-bar">
                <div
                  className="invoice-success-progress-bar-inner"
                  style={{ width: `${progress}%` }}
                >
                  {progress.toFixed(0)}%
                </div>
              </div>
            )}
          </div>
        )}
        {showModal && <ClaimSuccessModal onClose={handleModalClose} />}
        <div className="supportinvoice-header">
          {!isModalOpen && (
            <>
              {isMobile && (
                <div className="filter-toggle-container" onClick={toggleFilter}>
                  {showFilter ? (
                    <div className="filter-hide">
                      <FaTimesCircle />
                    </div>
                  ) : (
                    <div className="filter-show">
                      <SlEqualizer className="filter-toggle-icon" />
                      <span>Show Filters</span>
                    </div>
                  )}
                </div>
              )}
              {(showFilter || !isMobile) && (
                <div className="filter-overall-container">
                  <div className="winvoice-filter-container">
                    <div className="filter-worker-invoiceid">
                      <div className="invoice-filter-itemwn">
                        <label htmlFor="name-filter">Filter by client</label>
                        <input
                          id="name-filter"
                          type="text"
                          placeholder="Client name ..."
                          value={filterName}
                          onChange={handleFilterChange(setFilterName)}
                        />
                      </div>
                      <div className="invoice-filter-itemii">
                        <label htmlFor="invoice-id-filter">Invoice ID</label>
                        <input
                          id="invoice-id-filter"
                          type="text"
                          placeholder="Invoice ID ..."
                          value={filterInvoiceId}
                          onChange={handleFilterChange(setFilterInvoiceId)}
                        />
                      </div>
                    </div>
                    <div className="status-invoice-clear-container">
                      <div className="invoice-filter-itemds">
                        <label>Invoice date range</label>
                        <div className="invoice-date-selects">
                          <DatePicker
                            selected={filterStartDate}
                            onChange={(date) => setFilterStartDate(date)}
                            selectsStart
                            startDate={filterStartDate}
                            endDate={filterEndDate}
                            placeholderText="Start date"
                          />
                          <DatePicker
                            selected={filterEndDate}
                            onChange={(date) => setFilterEndDate(date)}
                            selectsEnd
                            startDate={filterStartDate}
                            endDate={filterEndDate}
                            placeholderText="End date"
                            minDate={filterStartDate}
                          />
                        </div>
                      </div>
                      <div className="status-invoice-status-container">
                        <div className="invoice-filter-itemst">
                          <label htmlFor="status-filter">Invoice status</label>
                          <select
                            id="status-filter"
                            value={filterStatus}
                            onChange={handleFilterChange(setFilterStatus)}
                          >
                            <option value="">All</option>
                            <option value="draft">Draft</option>
                            <option value="open">Open</option>
                            <option value="paid">Paid</option>
                          </select>
                        </div>
                        <div className="invoice-filter-itemst">
                          <label htmlFor="invoice-status-filter">
                            Invoice Sent
                          </label>
                          <select
                            id="invoice-status-filter"
                            value={filterInvoiceStatus}
                            onChange={handleInvoiceStatusFilterChange}
                          >
                            <option value="">All</option>
                            <option value="true">Sent</option>
                            <option value="false">Not Sent</option>
                          </select>
                        </div>
                      </div>
                      <div className="clear-filters-container">
                        <button
                          className="clear-filters-button"
                          onClick={handleClearFilters}
                        >
                          <FaRedo className="invoice-filter-reset" /> Clear
                          Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {loading ? (
          <div className="loading-spinner">
            <RotatingLines
              visible={true}
              height="96"
              width="56"
              color="grey"
              strokeWidth="3"
              animationDuration="0.75"
              ariaLabel="rotating-lines-loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="no-claims-message">
            <p>No matching Invoices found ...</p>
          </div>
        ) : (
          <>
            <table
              className={`supportclaim-support-table ${
                isModalOpen ? "hidden" : ""
              }`}
            >
              <thead>
                <tr>
                  <th>Invoice ID</th>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Worker</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Issued</th>
                  <th>Paid</th>
                  <th>View More</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, index) => {
                  return (
                    <tr
                      key={item.invoiceId || index}
                      ref={index === 0 ? tableRowRef : null}
                    >
                      <td className="invoice-idtd-style" data-th="invoice ID">
                        <span className="invoice-id-style">
                          {item.invoiceId}
                        </span>
                      </td>
                      <td data-th="Date">
                        {new Date(item.created * 1000).toLocaleDateString()}
                      </td>
                      <td data-th="Client">{item.clientName}</td>
                      <td data-th="Worker">{item.workerName}</td>
                      <td data-th="Status">
                        <div className="invoice-status-container">
                          {item.paymentStatus === "draft" ? (
                            <>
                              Draft
                              <FaExclamationCircle className="claim-status-pending" />
                            </>
                          ) : item.paymentStatus === "open" ? (
                            <>
                              Open{" "}
                              <GrPowerCycle className="claim-status-open" />
                            </>
                          ) : item.paymentStatus === "paid" ? (
                            <>
                              Paid
                              <FaCheckCircle className="claim-status-completed" />
                            </>
                          ) : item.paymentStatus === "void" ? (
                            <>
                              Void
                              <FaTimesCircle className="claim-status-void" />
                            </>
                          ) : item.paymentStatus === "uncollectible" ? (
                            <>
                              Uncollectible
                              <FaLock className="claim-status-uncollectible" />
                            </>
                          ) : null}
                        </div>
                      </td>
                      <td data-th="Amount">
                        <strong>${(item.amountDue / 100).toFixed(2)}</strong>
                      </td>
                      <td className="invoice-sent-style" data-th="Issued">
                        <strong
                          className={item.invoiceSent ? "yes-text" : "no-text"}
                        >
                          {item.invoiceSent ? "Yes" : "No"}
                        </strong>
                      </td>
                      <td className="invoice-sent-style" data-th="Paid">
                        <strong
                          className={item.invoicePaid ? "yes-text" : "no-text"}
                        >
                          {item.invoicePaid ? "Yes" : "No"}
                        </strong>
                      </td>
                      <td className="invoice-sent-style">
                        <button
                          className="view-more-button"
                          onClick={() => handleViewMore(item.invoiceId)}
                        >
                          View more
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="items-per-page-selector">
              <label htmlFor="items-per-page">Show items per page</label>
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                {dropdownOptions.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            {!isModalOpen && selectedInvoices.length > 0 && (
              <div className="generate-selected-invoices-container">
                <button
                  className="generate-selected-invoices-button"
                  onClick={handlePaySelectedInvoices}
                  disabled={isPaySelectedInvoicesDisabled}
                >
                  Pay Selected Invoices
                </button>
                {isPaySelectedInvoicesDisabled && (
                  <p>Only Open and finalized invoices can be paid</p>
                )}
              </div>
            )}

            {isModalOpen && selectedInvoice && (
              <div className="claim-details open">
                <div
                  className="claim-back-containeradmn"
                  onClick={handleBackClick}
                >
                  <FaArrowLeft className="claim-back-icon" /> Back to Invoices
                  List
                </div>
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Invoice ID</strong>
                      </td>
                      <td>{selectedInvoice.invoiceId}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Invoice Status</strong>
                      </td>
                      <td className="invoice-details-status">
                        {formatStatus(selectedInvoice.paymentStatus)}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Finalized On</strong>
                      </td>
                      <td className="td-date">
                        {new Date(
                          selectedInvoice.finalizedAt * 1000
                        ).toLocaleDateString()}
                      </td>
                    </tr>
                    {selectedInvoice.status === "paid" && (
                      <tr>
                        <td>
                          <strong>Paid On</strong>
                        </td>
                        <td className="td-date">
                          {new Date(
                            selectedInvoice.paidAt * 1000
                          ).toLocaleDateString()}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td>
                        <strong>Claim Reference Number</strong>
                      </td>
                      <td>
                        <strong>{selectedInvoice.claimReferenceNumber}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Agreement Reference Number</strong>
                      </td>
                      <td>
                        <a
                          href={`/account/admin-dashboard/agreements/${selectedInvoice.agreementId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="agreement-link"
                        >
                          {selectedInvoice.agreementReferenceNumber}
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Description</strong>
                      </td>
                      <td>
                        <strong>{selectedInvoice.description}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Client Name</strong>
                      </td>
                      <td>
                        <strong>
                          <strong>{selectedInvoice.clientName}</strong>
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Worker Name</strong>
                      </td>
                      <td>
                        <strong>{selectedInvoice.workerName}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Invoice Amount</strong>
                      </td>
                      <td className="claim-total-amount">
                        ${(selectedInvoice.amountDue / 100).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Issued</strong>
                      </td>
                      <td className="viewmore-td-style">
                        <strong>
                          {selectedInvoice.invoiceSent ? "Yes" : "No"}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Paid</strong>
                      </td>
                      <td className="viewmore-td-style">
                        {selectedInvoice.invoicePaid ? "Yes" : "No"}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="edit-button-cell">
                  <span className="claim-button-container">
                    <>
                      <button
                        className="claim-approve-button"
                        onClick={() => handlePayInvoice(selectedInvoice)}
                        disabled={
                          (selectedInvoice.status === "paid" ||
                            selectedInvoice.paymentStatus === "paid") &&
                          selectedInvoice.invoiceSent &&
                          selectedInvoice.invoicePaid
                        }
                      >
                        Pay Invoice
                      </button>
                    </>
                  </span>
                </div>
              </div>
            )}
            {!isModalOpen && (
              <div className="supportclaim-pagination-controls">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  <img src="/images/back-icon.png" alt="Previous" /> Previous
                </button>

                <span>
                  {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next <img src="/images/next-icon.png" alt="Next" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyInvoicesContent;
